import { IndicationUnit } from "@solomon-apps/shared/src/components/Metrics/IndicationUnit";
import { Badge } from "@solomon-apps/shared/src/components/ui/badge";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Card } from "@solomon-apps/shared/src/components/ui/card";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import { ArrowRightLongIcon } from "@solomon-labs/design/components/icons";
import { UploadFiles } from "@solomon-labs/design/components/ui";
import { motion } from "motion/react";
import type { ReactElement } from "react";

import type {
    OnboardingApplicationStatus,
    OnboardingStepView
} from "@/services/clientOnboarding";
import type { WalletView } from "@/services/clientWallets";

import { formatLongDate, formatRelativeDays } from "../../shared/format";
import { FundsNarrativeForm } from "../forms/FundsNarrativeForm";
import { LegalEntityForm } from "../forms/LegalEntityForm";
import { MsaSigningPanel } from "../forms/MsaSigningPanel";
import { OrderFormSigningPanel } from "../forms/OrderFormSigningPanel";
import { PersonDetail } from "../forms/PersonDetailForm";
import {
    ReviewAndSubmitPanel,
    ReviewSectionBlock
} from "../forms/ReviewAndSubmitPanel";
import { SignatoryAuthorityForm } from "../forms/SignatoryAuthorityForm";
import { SupportingDocsForm } from "../forms/SupportingDocsForm";
import { TaxIdForm } from "../forms/TaxIdForm";
import { UseCaseForm } from "../forms/UseCaseForm";
import { WalletForm } from "../forms/WalletForm";
import { useOnboardingApplicationContextOrNull } from "../OnboardingApplicationContext";
import {
    computeLockState,
    EstimateLine,
    PreconditionsLine,
    slotKey,
    StatusBadge,
    statusToIndicationState
} from "../OnboardingShared";
import {
    agreementsUnlocked,
    buildReviewSections,
    frozenSectionCopy,
    isSectionEditable
} from "../reviewSummary";
import type {
    ApplicationPhase,
    NextStepTarget,
    OnboardingNode,
    OnboardingSubsection,
    SectionStatus,
    Slot,
    SubsectionId
} from "../types";
import type { WalletEntry } from "../walletEntries";

import { DOCUMENT_ACCEPT, DOCUMENT_MAX_SIZE } from "./uploadConstraints";

// ─── SubsectionDetailPanel ──────────────────────────────────────────────────

export function SubsectionDetailPanel({
    node,
    applicationFrozen,
    applicationPhase,
    displayNumber,
    onOpen,
    onBegin,
    jumpToCard,
    onMarkComplete,
    nextStep,
    onContinue,
    walletEntries
}: {
    node: OnboardingNode;
    applicationFrozen: boolean;
    applicationPhase: ApplicationPhase;
    /** Section number prefix to render before the title — "2." for top-
     *  level subsections, "2.4" for child subsections that took a slot
     *  position from their parent. Null when the subsection has no
     *  numbering (e.g. before tree resolution). */
    displayNumber: string | null;
    onOpen: (id: SubsectionId) => void;
    onBegin: () => void;
    jumpToCard: (id: SubsectionId) => void;
    /** Mark the focused subsection as user-completed. Currently wired
     *  by UseCaseForm's Submit; could extend to other custom-form
     *  subsections that have a single-action submit (e.g. funds
     *  narrative). */
    onMarkComplete?: (id: SubsectionId) => void;
    /** Forward-CTA target — passed into each form so the Saved button
     *  flips to "Continue to {title}" once the form is committed and
     *  there's a next incomplete subsection. Null when the current
     *  subsection is the last incomplete one. */
    nextStep?: NextStepTarget | null;
    onContinue?: () => void;
    /** The hub's verified-wallet list, read back by Review and submit and
     *  by the frozen wallet section: the wallet step carries no payload. */
    walletEntries?: readonly WalletEntry[];
}): ReactElement {
    const subsection = node.subsection;
    const lock = computeLockState(node, applicationFrozen);
    const { hasPreconditions, allPreconditionsMet, isLocked, interactive } =
        lock;
    // The live application decides what is still editable and whether the
    // agreements have been sent. Absent on the mock-scenario dev path
    // (`?scenario=N`), which renders every form as if in draft.
    const applicationContext = useOnboardingApplicationContextOrNull();
    const applicationStatus = applicationContext?.application.status;
    const applicationSteps = applicationContext?.steps ?? [];
    const frozenSection =
        applicationStatus !== undefined &&
        !isSectionEditable(applicationStatus, subsection.id);

    // Title + description render in their default tones regardless of
    // lock state — the StatusBadge in the header's right slot is the
    // sole state cue. Standardized away from the old 3-tier `titleTone`
    // per May 2026 cleanup.

    // When a subsection has sub-rows, the sub-rows own every field: the rail
    // is the complete map of the work and each row is where that work
    // happens. Rendering the form here too would (a) duplicate what the
    // sub-rows show and (b) let someone finish the section without ever
    // clicking a row, which hides the structure the rail is advertising.
    // Sections with no sub-rows keep rendering their form directly.
    // > 1, not > 0: a lone sub-row just restates its section, so it adds no
    // navigation and the section should render its form directly (§3 Tax ID).
    const hasSubRows = subsection.slots.length > 1 && !subsection.slotsAsItems;

    // Review and submit renders the whole application, so it owns its
    // panel outright — there is no per-slot shape underneath it.
    if (subsection.id === "review-submit") {
        return (
            <ReviewAndSubmitPanel
                key={subsection.id}
                jumpToCard={jumpToCard}
                walletEntries={walletEntries}
            />
        );
    }

    // §D.1 MSA and §D.2 Minting Master Agreement. Both go through the lock
    // branch: they are sent by the issuer's approval, so before that there
    // is nothing for the applicant to do and the panel says so rather than
    // offering an action that would be refused.
    if (subsection.id === "msa" || subsection.id === "order-form") {
        if (!agreementsUnlocked(applicationStatus)) {
            return (
                <AgreementLockedPanel
                    key={subsection.id}
                    title={subsection.title}
                />
            );
        }
        return subsection.id === "msa" ? (
            <MsaSigningPanel
                key={subsection.id}
                displayNumber={displayNumber}
            />
        ) : (
            <OrderFormSigningPanel
                key={subsection.id}
                displayNumber={displayNumber}
            />
        );
    }

    // A submitted application is a snapshot the reviewer is reading, so
    // every applicant section shows its saved values back instead of a form
    // that the API would refuse. The Authorized Signatory stays editable
    // while the agreements are out — that is where a wrong signing address
    // gets corrected.
    if (frozenSection) {
        return (
            <FrozenSectionPanel
                key={subsection.id}
                subsectionId={subsection.id}
                title={subsection.title}
                status={applicationStatus}
                steps={applicationSteps}
                walletEntries={walletEntries}
            />
        );
    }

    // Use Case & Intent is a 3-question form (no documents). Render the
    // custom UseCaseForm panel in place of the standard subsection panel.
    if (!hasSubRows && subsection.id === "use-case") {
        return (
            <UseCaseForm
                key={subsection.id}
                onSubmit={() => {
                    onMarkComplete?.(subsection.id);
                }}
                nextStep={nextStep ?? null}
                onContinue={onContinue}
            />
        );
    }

    // §B.1 Legal Entity, §B.2 Tax ID, §B.4 Authorized Signatory each
    // render a dedicated structured form (replacing the generic
    // accepted-docs panel) because they bundle multiple required fields
    // with their uploads.
    if (!hasSubRows && subsection.id === "legal-entity") {
        return (
            <LegalEntityForm
                key={subsection.id}
                nextStep={nextStep ?? null}
                onContinue={onContinue}
            />
        );
    }
    if (!hasSubRows && subsection.id === "tax-identification") {
        return (
            <TaxIdForm
                key={subsection.id}
                nextStep={nextStep ?? null}
                onContinue={onContinue}
            />
        );
    }
    if (!hasSubRows && subsection.id === "signatory-authority") {
        return (
            <SignatoryAuthorityForm
                key={subsection.id}
                nextStep={nextStep ?? null}
                onContinue={onContinue}
            />
        );
    }
    return (
        <motion.div
            key={subsection.id}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: easings.outCubic }}
        >
            <Heading
                title={subsection.title}
                actions={
                    <StatusBadge
                        subsection={subsection}
                        isLocked={isLocked}
                        phase={applicationPhase}
                    />
                }
                className="pb-4 border-b border-line-base"
            />

            <Text
                variant="small"
                className="block leading-relaxed pt-4 text-foreground-muted"
            >
                {subsection.description}
            </Text>

            {/* Locked subsection with explicit preconditions — render
                them as a sunken+border card (sister to the Accepted
                Documents card on the slot panel), then a disabled
                primary CTA below. The PreconditionsLine + EstimateLine
                duplicate is suppressed here. */}
            {isLocked && hasPreconditions ? (
                <>
                    <Card
                        surface="sunken"
                        border="error"
                        className="px-5 py-5 mt-8"
                    >
                        <Heading
                            title="Complete these sections first"
                            titleVariant="h3"
                            tone="error"
                            className="pb-3"
                        />
                        <ul className="space-y-2">
                            {node.preconditions!.map((p) => (
                                <li
                                    key={p.refId}
                                    className="flex items-center gap-2"
                                >
                                    <IndicationUnit
                                        variant="status"
                                        state={statusToIndicationState(
                                            p.status,
                                            false
                                        )}
                                        size={6}
                                    />
                                    <Button
                                        type="button"
                                        variant="transparent"
                                        interaction="underline"
                                        size="fit"
                                        onClick={() => jumpToCard(p.refId)}
                                        className="text-left text-foreground-base hover:text-accent-base"
                                    >
                                        <Text
                                            variant="small"
                                            render={<span />}
                                            className="leading-snug"
                                        >
                                            {p.title}
                                        </Text>
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    </Card>

                    <div className="pt-12 max-w-sm mx-auto">
                        <Button
                            variant="raised"
                            interaction="lifted"
                            size="sm"
                            fullWidth
                            disabled
                            trailing={
                                isAddListCta(subsection) ? undefined : (
                                    <ArrowRightLongIcon className="size-5" />
                                )
                            }
                        >
                            {subsection.verification
                                ? subsection.verification.ctaLabel
                                : ctaLabel(subsection)}
                        </Button>
                    </div>
                </>
            ) : (
                <>
                    {hasPreconditions &&
                        allPreconditionsMet &&
                        subsection.status === "WaitingOnYou" && (
                            <PreconditionsLine
                                preconditions={node.preconditions!}
                                allMet
                                onJump={jumpToCard}
                                tone="ready"
                            />
                        )}

                    <EstimateLine subsection={subsection} locked={isLocked} />

                    {interactive && (
                        <div className="pt-12 max-w-sm mx-auto">
                            <Button
                                variant="raised"
                                interaction="lifted"
                                size="sm"
                                fullWidth
                                trailing={
                                    isAddListCta(subsection) ? undefined : (
                                        <ArrowRightLongIcon className="size-5" />
                                    )
                                }
                                onClick={() => {
                                    // Subsection-level verification (e.g.
                                    // MSA → e-sign provider): the
                                    // subsection IS the action, route
                                    // straight to the provider. No slot
                                    // advance needed.
                                    if (subsection.verification) {
                                        onOpen(subsection.id);
                                        return;
                                    }
                                    onBegin();
                                    onOpen(subsection.id);
                                }}
                            >
                                {subsection.verification
                                    ? subsection.verification.ctaLabel
                                    : ctaLabel(subsection)}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </motion.div>
    );
}

// ─── SlotDetailPanel ────────────────────────────────────────────────────────

/**
 * Effective subsection for a slot's status badge in the detail panel.
 * Post-submission, a slot's badge follows its own reviewState; pre-submission
 * (no reviewState) it inherits the parent's status so both the section list
 * row and the open detail panel agree.
 */
function slotEffectiveSubsection(
    parent: OnboardingSubsection,
    slot: Slot
): OnboardingSubsection {
    if (!slot.reviewState) return parent;
    const status: SectionStatus =
        slot.reviewState === "Approved"
            ? "Complete"
            : slot.reviewState === "Rejected"
              ? "NeedsAttention"
              : "WaitingOnSolomon";
    return { ...parent, status };
}

export function SlotDetailPanel({
    node,
    applicationFrozen,
    applicationPhase,
    focusedSlot,
    slotNumberByKey,
    onDeleteWallet,
    onDeletePerson,
    uboEntries,
    onUboNameChange,
    walletEntries,
    onWalletLabelChange,
    onWalletVerified,
    nextStep,
    onContinue
}: {
    node: OnboardingNode;
    applicationFrozen: boolean;
    applicationPhase: ApplicationPhase;
    focusedSlot: { parentId: SubsectionId; slotIdx: number };
    slotNumberByKey: Map<string, string>;
    /** Delete handlers for hub-managed item lists (wallets / UBOs).
     *  Synthetic-slot lists own their own state in OnboardingPanel, so the
     *  detail panel routes the click here rather than mutating data. */
    onDeleteWallet?: (idx: number) => void;
    onDeletePerson?: (idx: number) => void;
    /** Single typed entry array per list (Phase F): each entry carries
     *  its stable uuid + the working label that flows into the
     *  synthetic-slot title. The form receives the entire entry +
     *  index so dirty detection has the right starting point. */
    uboEntries?: { entryUuid: string; legalName: string }[];
    onUboNameChange?: (idx: number, name: string) => void;
    walletEntries?: WalletEntry[];
    onWalletLabelChange?: (idx: number, label: string) => void;
    onWalletVerified: (idx: number, wallet: WalletView) => void;
    /** Forward-CTA target — see SubsectionDetailPanel for full
     *  semantics. Threaded into LegalEntity / TaxId / Signatory /
     *  FundsNarrative forms so their post-save state offers a
     *  "Continue" affordance instead of the dead-end "Saved" button.
     *  Per-entry forms (PersonDetail, Wallet) are intentionally
     *  exempt since "next" is ambiguous mid-list. */
    nextStep?: NextStepTarget | null;
    onContinue?: () => void;
}): ReactElement {
    const subsection = node.subsection;
    const lock = computeLockState(node, applicationFrozen);
    // Same freeze as the section panel: a sub-row of a submitted section
    // must not offer a form the API would refuse. Reading the whole
    // section's record back is more use than one row of it, so the sub-row
    // shows what the section panel shows.
    const applicationContext = useOnboardingApplicationContextOrNull();
    const applicationStatus = applicationContext?.application.status;
    const frozenSection =
        applicationStatus !== undefined &&
        !isSectionEditable(applicationStatus, subsection.id);

    const { parentId: keyParentId, slotIdx } = focusedSlot;
    const focusedSlotKey = slotKey(keyParentId, slotIdx);
    // Guard: the focused parent should match our node's subsection id, but
    // it may not if a subsection row was clicked between renders.
    const slot: Slot | undefined =
        keyParentId === subsection.id ? subsection.slots[slotIdx] : undefined;
    const number = slotNumberByKey.get(focusedSlotKey);

    if (!slot || !number) {
        // Bail out — the focused slot doesn't belong to the focused
        // subsection. This shouldn't happen in practice but protects
        // against transient mismatches.
        return <EmptyDetailPanel />;
    }

    if (frozenSection) {
        return (
            <FrozenSectionPanel
                key={subsection.id}
                subsectionId={subsection.id}
                title={subsection.title}
                status={applicationStatus}
                steps={applicationContext?.steps ?? []}
                walletEntries={walletEntries}
            />
        );
    }

    // §B.1 Legal Entity / §B.2 Tax ID / §B.4 Authorized Signatory bundle
    // their fields + doc uploads into one structured form rather than
    // per-slot uploads. Render that form regardless of which slot the
    // user navigated into — keeps the left-list IA (slot rows) while
    // collapsing data entry into a single panel.
    if (subsection.id === "legal-entity") {
        return (
            <LegalEntityForm
                key={focusedSlotKey}
                slotIdx={slotIdx}
                nextStep={nextStep ?? null}
                onContinue={onContinue}
            />
        );
    }
    if (subsection.id === "tax-identification") {
        return (
            <TaxIdForm
                key={focusedSlotKey}
                nextStep={nextStep ?? null}
                onContinue={onContinue}
            />
        );
    }
    if (subsection.id === "signatory-authority") {
        return (
            <SignatoryAuthorityForm
                key={focusedSlotKey}
                slotIdx={slotIdx}
                nextStep={nextStep ?? null}
                onContinue={onContinue}
            />
        );
    }

    // §B.9 Authorized Operators — per-operator entries, the same
    // synthetic-slot list shape as §B.5 Ultimate Beneficiaries below.
    // Source-of-funds slots render structured forms instead of the
    // generic accepted-docs panel. Slot 5.1 is the required narrative
    // form (two textareas); slot 5.2 is the optional supporting-docs
    // upload area.
    if (subsection.id === "source-of-funds") {
        if (slotIdx === 0) {
            return (
                <FundsNarrativeForm
                    key={focusedSlotKey}
                    slotTitle={slot.title}
                    // The form saves the step itself; the panel has
                    // nothing more to do once it reports back.
                    onSubmit={(): void => undefined}
                    nextStep={nextStep ?? null}
                    onContinue={onContinue}
                />
            );
        }
        if (slotIdx === 1) {
            return (
                <SupportingDocsForm
                    key={focusedSlotKey}
                    slotTitle={slot.title}
                    // The form uploads and saves itself; the panel has
                    // nothing more to do once it reports back.
                    onSubmit={(): void => undefined}
                />
            );
        }
    }

    // Wallet-primary slots render a structured form instead of the generic
    // accepted-docs panel. The form is a single submission per wallet.
    if (subsection.id === "wallet-primary") {
        const walletEntry = walletEntries?.[slotIdx];
        return (
            <WalletForm
                key={focusedSlotKey}
                slotKey={focusedSlotKey}
                slotTitle={slot.title}
                wallet={walletEntry?.wallet ?? null}
                label={walletEntry?.label ?? ""}
                onLabelChange={(next) => onWalletLabelChange?.(slotIdx, next)}
                onSubmit={(wallet) => onWalletVerified(slotIdx, wallet)}
                onDelete={
                    onDeleteWallet
                        ? (): void => onDeleteWallet(slotIdx)
                        : undefined
                }
                nextStep={nextStep ?? null}
                onContinue={onContinue}
            />
        );
    }

    // Beneficial-ownership slots are per-Person — each beneficiary needs
    // just a name and ownership %. Sanctions screening runs Solomon-side
    // and doesn't render as a step. (Per BD: "Beneficial owners (UBOs
    // ≥25%) — OFAC compliance" — no Persona KYC per UBO.)
    if (subsection.id === "beneficial-ownership") {
        const uboEntry = uboEntries?.[slotIdx];
        return (
            <PersonDetail
                key={focusedSlotKey}
                personLabel={slot.title}
                legalName={uboEntry?.legalName ?? ""}
                onLegalNameChange={(name) => onUboNameChange?.(slotIdx, name)}
                entryUuid={uboEntry?.entryUuid}
                // The form saves the entry itself; the panel has nothing
                // more to do once it reports back.
                onSubmit={(): void => undefined}
                onDelete={
                    onDeletePerson
                        ? (): void => onDeletePerson(slotIdx)
                        : undefined
                }
            />
        );
    }

    // Render alternatives as a bullet list when the slot has `acceptable`
    // entries; otherwise the slot title itself is the single accepted doc.
    const acceptedDocs: string[] =
        slot.acceptable && slot.acceptable.length > 0
            ? slot.acceptable
            : [slot.title];

    return (
        <motion.div
            key={focusedSlotKey}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: easings.outCubic }}
        >
            {/* Title + status + divider live directly on bg-surface-ground.
                The h1 + rule reads as the section header for the slot
                detail. The slot's status badge mirrors the subsection
                detail panel layout so both panels share the same anatomy. */}
            <Heading
                title={slot.title}
                actions={
                    slot.issuerRequested ? (
                        <Badge variant="error">Additional Info Needed</Badge>
                    ) : slot.reviewState === "Rejected" ? (
                        <Badge variant="error">Rejected</Badge>
                    ) : (
                        <StatusBadge
                            subsection={slotEffectiveSubsection(
                                subsection,
                                slot
                            )}
                            isLocked={lock.isLocked}
                            phase={applicationPhase}
                        />
                    )
                }
                className="pb-4 border-b border-line-base"
            />

            {slot.description && (
                <Text
                    variant="small"
                    className="block leading-relaxed text-foreground-muted pt-4"
                >
                    {slot.description}
                </Text>
            )}

            {slot.issuerRequested && (
                <Card border="error" className="px-5 py-5 mt-6">
                    {/* TODO: replace "Solomon Labs Request" with
                        `me.org.issuer.name` once the /me response includes
                        the client's issuer relationship — same TODO as
                        Layout.tsx. */}
                    <Heading
                        title="Solomon Labs Request"
                        titleVariant="h3"
                        tone="error"
                        className="pb-3"
                        actions={
                            slot.issuerRequested.deadline && (
                                <Text
                                    variant="h3"
                                    render={<span />}
                                    className="leading-tight text-error-base shrink-0"
                                >
                                    Due{" "}
                                    {formatLongDate(
                                        slot.issuerRequested.deadline
                                    )}{" "}
                                    (
                                    {formatRelativeDays(
                                        slot.issuerRequested.deadline
                                    )}
                                    )
                                </Text>
                            )
                        }
                    />
                    <Text
                        variant="small"
                        className="block leading-relaxed text-foreground-base"
                    >
                        {slot.description}
                    </Text>
                    <Text
                        variant="label"
                        className="block leading-none text-foreground-muted pt-3"
                    >
                        Requested{" "}
                        {formatLongDate(slot.issuerRequested.requestedAt)}
                    </Text>
                </Card>
            )}

            {slot.reviewState === "Rejected" && slot.reviewerNote && (
                <Card border="error" className="px-5 py-5 mt-6">
                    {/* TODO: replace "Solomon Labs Note" with
                        `me.org.issuer.name` once the /me response includes
                        the client's issuer relationship — same TODO as
                        Layout.tsx. */}
                    <Heading
                        title="Solomon Labs Note"
                        titleVariant="h3"
                        tone="error"
                        className="pb-3"
                        actions={
                            slot.rejection?.deadline && (
                                <Text
                                    variant="h3"
                                    render={<span />}
                                    className="leading-tight text-error-base shrink-0"
                                >
                                    Due{" "}
                                    {formatLongDate(slot.rejection.deadline)} (
                                    {formatRelativeDays(
                                        slot.rejection.deadline
                                    )}
                                    )
                                </Text>
                            )
                        }
                    />
                    <Text
                        variant="small"
                        className="block leading-relaxed text-foreground-base"
                    >
                        {slot.reviewerNote}
                    </Text>
                    {slot.rejection?.rejectedAt && (
                        <Text
                            variant="label"
                            className="block leading-none text-foreground-muted pt-3"
                        >
                            Rejected {formatLongDate(slot.rejection.rejectedAt)}
                        </Text>
                    )}
                </Card>
            )}

            {slot.verification ? (
                <>
                    {/* Provider notice — raised card to mirror the docs
                        card on the upload branch. */}
                    <Card border={false} className="px-5 py-5 mt-8">
                        <Text
                            variant="label"
                            className="block text-foreground-neutral pb-2"
                        >
                            Verified via {slot.verification.provider}
                        </Text>
                        <Text
                            variant="small"
                            className="block leading-relaxed text-foreground-muted"
                        >
                            This step routes you to {slot.verification.provider}
                            ; complete the flow there and we&apos;ll pick up the
                            result automatically.
                        </Text>
                        {slot.conditional && (
                            <Text
                                variant="label"
                                className="block leading-snug text-foreground-muted pt-2"
                            >
                                {slot.conditional.description}
                            </Text>
                        )}
                    </Card>

                    {/* CTA on bg-surface-ground, half-width centered. */}
                    <div className="pt-12 max-w-sm mx-auto">
                        <Button
                            variant="raised"
                            interaction="lifted"
                            size="sm"
                            fullWidth
                            trailing={<ArrowRightLongIcon className="size-5" />}
                            // Placeholder: the slot-level provider hand-off is
                            // not wired yet, so the CTA does nothing.
                            onClick={(): void => undefined}
                        >
                            {slot.verification.ctaLabel}
                        </Button>
                    </div>
                </>
            ) : (
                <>
                    {/* Accepted Documents — raised card. Header pluralizes
                        based on the count, except when the slot's
                        `examplesOnly` flag is set: then the items are
                        illustrative variants ("Examples of Accepted
                        Documents"). */}
                    <Card border={false} className="px-5 py-5 mt-8">
                        <Heading
                            title={
                                slot.examplesOnly
                                    ? "Examples of Accepted Documents"
                                    : acceptedDocs.length === 1
                                      ? "Accepted Document"
                                      : "Accepted Documents"
                            }
                            titleVariant="h3"
                            className="pb-3"
                        />
                        <ul className="space-y-1.5">
                            {acceptedDocs.map((doc) => (
                                <li
                                    key={doc}
                                    className="flex items-center gap-2"
                                >
                                    <IndicationUnit
                                        variant="status"
                                        state="disabled"
                                        size={6}
                                    />
                                    <Text
                                        variant="small"
                                        className="leading-none text-foreground-base"
                                    >
                                        {doc}
                                    </Text>
                                </li>
                            ))}
                        </ul>
                        {slot.conditional && (
                            <Text
                                variant="label"
                                className="block leading-snug text-foreground-muted pt-2"
                            >
                                {slot.conditional.description}
                            </Text>
                        )}
                    </Card>

                    <div className="pt-12">
                        <UploadFiles
                            className="max-w-sm mx-auto"
                            size="sm"
                            accept={DOCUMENT_ACCEPT}
                            maxSize={DOCUMENT_MAX_SIZE}
                            // Placeholder: generic per-slot uploads are not
                            // wired yet, so a selection is dropped.
                            onSelect={(): void => undefined}
                        />
                    </div>
                </>
            )}
        </motion.div>
    );
}

// ─── EmptyDetailPanel ───────────────────────────────────────────────────────

export function EmptyDetailPanel(): ReactElement {
    return (
        <Card border={false} className="px-5 py-5">
            <Text
                variant="small"
                className="leading-relaxed text-foreground-muted"
            >
                Select a subsection to see its proof points and accepted
                artifacts.
            </Text>
        </Card>
    );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function ctaLabel(subsection: OnboardingSubsection): string {
    const { status, id } = subsection;
    // Per-subsection CTAs that read more naturally than the generic verbs.
    // Wallets / Beneficiaries are "lists the user is building up" — the
    // primary action is to add another, regardless of status. The "+"
    // glyph signals the add semantics; the canonical Button's trailing
    // arrow is intentionally omitted at the call site for these two so
    // the label stands alone (no "+Add Wallet →").
    if (id === "wallet-primary") {
        return "+Add Wallet";
    }
    if (id === "beneficial-ownership") {
        return "+Add Person";
    }
    if (status === "InProgress" || status === "WaitingOnYou") return "Continue";
    if (status === "NeedsAttention") return "Resolve";
    if (status === "Complete") return "Review";
    return "Begin";
}

/** True for subsections whose CTA is a "+ add to list" action — those
 *  buttons render WITHOUT the standard trailing arrow because the "+"
 *  in the label is the action signal. */
function isAddListCta(subsection: OnboardingSubsection): boolean {
    return (
        subsection.id === "wallet-primary" ||
        subsection.id === "beneficial-ownership"
    );
}

// ─── AgreementLockedPanel ───────────────────────────────────────────────────

/**
 * What the applicant sees on an agreement before it has been sent.
 *
 * The two agreements are the one part of onboarding the applicant does not
 * start: the issuer's approval is what puts them in front of the authorized
 * signatory. Saying so is the whole panel — an action here would be refused,
 * and a blank card would read as something broken.
 */
function AgreementLockedPanel({ title }: { title: string }): ReactElement {
    return (
        <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: easings.outCubic }}
        >
            <Heading title={title} className="pb-4 border-b border-line-base" />
            <Card surface="sunken" className="px-5 py-5 mt-8">
                <Heading
                    title="Unlocks once Solomon has reviewed your application"
                    titleVariant="h3"
                    className="pb-2"
                />
                <Text
                    variant="small"
                    className="block leading-relaxed text-foreground-muted"
                >
                    Submit your application first. Once it is approved, Solomon
                    sends this agreement to your authorized signatory for
                    e-signature and you can track it here.
                </Text>
            </Card>
        </motion.div>
    );
}

// ─── FrozenSectionPanel ─────────────────────────────────────────────────────

/**
 * A submitted section, read back rather than re-offered.
 *
 * The reviewer is reading a snapshot, so the API refuses edits; showing the
 * form anyway would invite work that cannot be saved. The values come from
 * the same builder the Review and submit section uses, so what the applicant
 * checked before submitting is literally what they see afterwards.
 */
function FrozenSectionPanel({
    subsectionId,
    title,
    status,
    steps,
    walletEntries = []
}: {
    subsectionId: SubsectionId;
    title: string;
    status: OnboardingApplicationStatus;
    steps: OnboardingStepView[];
    walletEntries?: readonly WalletEntry[];
}): ReactElement {
    const section = buildReviewSections(steps, walletEntries).find(
        (candidate) => candidate.subsectionId === subsectionId
    );
    return (
        <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: easings.outCubic }}
        >
            <Heading title={title} className="pb-4 border-b border-line-base" />
            <Text
                variant="small"
                className="block leading-relaxed pt-4 text-foreground-muted"
            >
                {frozenSectionCopy(status)}
            </Text>
            {section ? (
                <div className="pt-4">
                    <ReviewSectionBlock section={section} editable={false} />
                </div>
            ) : (
                <Text
                    variant="small"
                    className="block leading-relaxed pt-8 text-foreground-muted"
                >
                    Nothing was saved in this section.
                </Text>
            )}
        </motion.div>
    );
}
